/**
 * Шапка профиля: аватар облика, ник, уровень, деньги, щит новичка.
 *
 * Назначение файла: держать игрока и его деньги на виду на каждом экране и
 * давать короткий путь к экрану «Герой». Ничего не считает: все числа приходят
 * с сервера (закон 5), а облик берётся из настроек устройства.
 *
 * Импорты: форматтеры из @hive/i18n, список обликов из `game/appearance`,
 * представление кошелька из игрового слоя.
 */

import { formatCrystals, formatCrystalsSigned, type LocaleCode } from "@hive/i18n";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { heroAppearance, type HeroAppearanceId } from "../../game/appearance.js";
import { Crystals } from "../CrystalIcon.jsx";
import type { WalletView } from "../../game/economy.js";
import "./profile.css";

export interface ProfileBarProps {
  readonly locale: LocaleCode;
  readonly appearanceId: HeroAppearanceId;
  readonly nickname: string;
  readonly level: number;
  readonly atCap: boolean;
  /** Активный щит новичка: метка в шапке, без времени — оно на экране героя. */
  readonly shielded: boolean;
  /**
   * Кошелёк показывается только там, где нет его собственной панели. На карте
   * она есть и показывает оба субсчёта с долгом: второй раз то же число сбивает.
   */
  readonly wallet: WalletView | null;
  readonly onOpen: () => void;
}

export function ProfileBar({
  locale,
  appearanceId,
  nickname,
  level: heroLevel,
  atCap,
  shielded,
  wallet,
  onOpen,
}: ProfileBarProps): React.JSX.Element {
  const { t } = useTranslation(["hero", "economy"]);
  const [broken, setBroken] = useState(false);
  const appearance = heroAppearance(appearanceId);

  return (
    <button type="button" className="profile-bar" onClick={onOpen} aria-label={t("hero:bar.open")}>
      <span className="profile-bar__face-slot">
        {broken ? (
          <span className="profile-bar__face-fallback" aria-hidden="true">
            {[...nickname.trim()].slice(0, 1).join("").toUpperCase()}
          </span>
        ) : (
          <img
            className="profile-bar__face"
            src={appearance.avatar}
            alt=""
            width={192}
            height={192}
            decoding="async"
            onError={() => setBroken(true)}
          />
        )}
        <span className="profile-bar__level">{heroLevel}</span>
      </span>

      <span className="profile-bar__who">
        {/* i18n-ignore-start: ник игрока — данные, а не надпись интерфейса */}
        <b className="profile-bar__nickname">{nickname}</b>
        {/* i18n-ignore-end */}
        <small className="profile-bar__rank">
          {atCap ? t("hero:level_max") : t("hero:level", { level: heroLevel })}
        </small>
      </span>

      {shielded && <span className="profile-bar__shield">{t("hero:shield_short")}</span>}

      {wallet !== null && (
        <span className="profile-bar__money">
          {wallet.debt > 0 ? (
            <b className="profile-bar__amount profile-bar__amount--debt">
              <Crystals>{formatCrystalsSigned(locale, -wallet.debt, "hive")}</Crystals>
            </b>
          ) : (
            <b className="profile-bar__amount">
              <Crystals>{formatCrystals(locale, wallet.hive, "hive")}</Crystals>
            </b>
          )}
          <small className="profile-bar__money-label">
            {wallet.debt > 0 ? t("economy:debt.title") : t("economy:wallet.hive")}
          </small>
        </span>
      )}
    </button>
  );
}
